import { UserDataDisplay } from "#src/components/Common/UserDataDisplay";
import { UserGroupDataDisplay } from "#src/components/Common/UserGroupDataDisplay";
import { useListUserGroups } from "#src/components/hooks/adapters/useUserGroups";
import { useListUsers } from "#src/components/hooks/adapters/useUsers";
import { useHasPermission } from "#src/contexts/AuthenticatedContext.helpers";
import {
  Accordion,
  ArrayDataDisplay,
  KeyValueList,
  KeyValuePairList,
  TextDataDisplay,
} from "@validereinc/common-components";
import { UserGroupType, UserType } from "@validereinc/domain";
import classNames from "classnames/bind";
import React, { useMemo } from "react";
import styles from "./WorkflowActionSendNotification.module.scss";

const cx = classNames.bind(styles);

const { AccordionPanel } = Accordion;

export const WorkflowActionSendNotification = ({
  users,
  groups,
  message,
  title,
}: {
  users: string[];
  groups: string[];
  message: string;
  title: string;
}) => {
  const [canReadUserGroups] = useHasPermission("user_groups:read");
  const usersQuery = useListUsers({
    filters: {
      id: {
        $in: users,
      },
    },
  });
  const groupsQuery = useListUserGroups(
    {
      filters: {
        id: {
          $in: groups,
        },
      },
    },
    { enabled: canReadUserGroups && groups.length > 0 }
  );
  const usersMap = useMemo(() => {
    const users = usersQuery.data?.data ?? [];
    return users.reduce<Record<string, UserType>>((map, user) => {
      map[user.id] = user;
      return map;
    }, {});
  }, [usersQuery]);
  const groupsMap = useMemo(() => {
    const groups = groupsQuery.data?.data ?? [];
    return groups.reduce<Record<string, UserGroupType>>((map, group) => {
      map[group.id] = group;
      return map;
    }, {});
  }, [groupsQuery]);
  const contentData: KeyValuePairList = [
    {
      title: "Subject",
      value: <TextDataDisplay value={title} />,
    },
    {
      title: "Body",
      value: <TextDataDisplay value={message} />,
    },
  ];
  const recipientsData: KeyValuePairList = [
    {
      title: "Users",
      value: (
        <ArrayDataDisplay
          value={users.map((id) => (
            <UserDataDisplay
              key={id}
              id={id}
              name={usersMap[id]?.name}
              isInline
            />
          ))}
          isLoading={usersQuery.isLoading}
        />
      ),
    },
    {
      title: "Groups",
      value: (
        <ArrayDataDisplay
          value={groups.map((groupId) => (
            <UserGroupDataDisplay
              key={groupId}
              id={groupId}
              name={groupsMap[groupId]?.name}
              isInline
            />
          ))}
          isLoading={groupsQuery.isLoading}
        />
      ),
    },
  ];

  return (
    <Accordion defaultActiveKeys={["content", "recipients"]}>
      <AccordionPanel
        dataKey="content"
        title="Content"
      >
        <KeyValueList
          className={cx("keyValueListContainer")}
          data={contentData}
        />
      </AccordionPanel>
      <AccordionPanel
        dataKey="recipients"
        title="Recipients"
      >
        <KeyValueList
          className={cx("keyValueListContainer")}
          data={recipientsData}
        />
      </AccordionPanel>
    </Accordion>
  );
};
